import { useMemo } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { useAlert } from "../../../common/Alert";
import { useIntl } from "react-intl";
import { MoreItem } from "../../../common/navigationHeaderOptions";
import { useIntlTokens } from "../../../components/mobile/useIntlTokens";
import { useTodoEditMoreMenuItemsDeleteMutation } from "./__generated__/useTodoEditMoreMenuItemsDeleteMutation.graphql";
import { useTodoEditMoreMenuItems_todo$key } from "./__generated__/useTodoEditMoreMenuItems_todo.graphql";

export function useTodoEditMoreMenuItems({
  todo: todoKey,
  close,
  onMoveToCategory,
}: {
  todo: useTodoEditMoreMenuItems_todo$key | null;
  close: () => void;
  onMoveToCategory: (todoId: string) => void;
}) {
  const todo = useFragment(
    graphql`
      fragment useTodoEditMoreMenuItems_todo on Todo {
        id
        title
        isOrg
      }
    `,
    todoKey
  );

  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const alert = useAlert();
  const [deleteTodo] = useMutation<useTodoEditMoreMenuItemsDeleteMutation>(
    graphql`
      mutation useTodoEditMoreMenuItemsDeleteMutation($todoId: ID!) {
        todo(todoId: $todoId) {
          deleteTodoItem {
            todo {
              id @deleteRecord
              taskExecution {
                todos {
                  id
                }
              }
            }
          }
        }
      }
    `
  );

  const deleteMenuItem: MoreItem = useMemo(
    () => ({
      icon: "trash",
      label: intl_tokens.Delete,
      color: "negative",
      type: "button",
      onPress: () => {
        if (!todo) return;
        alert.alert(
          intl_tokens.Delete,
          intl.formatMessage(
            {
              defaultMessage: "Are you sure you want to delete {title}?",
              description: "TodoEditMain alert delete message",
            },
            {
              title: todo.title,
            }
          ),
          [
            {
              text: intl_tokens.Cancel,
              style: "cancel",
            },
            {
              text: intl_tokens.Delete,
              style: "destructive",
              onPress: () => {
                deleteTodo({
                  variables: {
                    todoId: todo.id,
                  },
                  onCompleted: () => {
                    close();
                  },
                });
              },
            },
          ]
        );
      },
    }),
    [
      alert,
      close,
      deleteTodo,
      intl,
      intl_tokens.Cancel,
      intl_tokens.Delete,
      todo,
    ]
  );

  const moveTodoMoreButtonTitle = intl.formatMessage({
    defaultMessage: "Move to another list",
    description: "TodoEditMain move todo more button title",
  });

  const moveMenuItem: MoreItem = useMemo(
    () => ({
      icon: "todo-category",
      label: moveTodoMoreButtonTitle,
      onPress: () => {
        if (!todo) return;
        onMoveToCategory(todo.id);
      },
    }),
    [moveTodoMoreButtonTitle, onMoveToCategory, todo]
  );

  return useMemo(() => {
    if (!todo) return [];
    const items: MoreItem[] = [];

    if (!todo.isOrg) {
      items.push(deleteMenuItem);
    }

    items.push(moveMenuItem);

    if (items.length === 0) {
      return undefined;
    }
    return items;
  }, [deleteMenuItem, moveMenuItem, todo]);
}
